import React from 'react'
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { CoachAvatar } from '@src/components/Avatar/CoachAvatar'
import { Icon, IconName, Text } from '@src/components/base'

interface CoachProps {
  accessibilityLabel: string
  badgeLabel?: string
  iconBackgroundStyle?: StyleProp<ViewStyle>
  iconName?: IconName
  iconSize?: number
  hasUnreadMessage?: boolean
  onChatPress?: () => void
  onPress?: () => void
  photo?: string | null
  subtitle: string
  title: string
}

export const Coach = ({
  accessibilityLabel,
  badgeLabel,
  iconBackgroundStyle,
  iconName,
  iconSize = 32,
  hasUnreadMessage = false,
  onChatPress,
  onPress,
  photo,
  subtitle,
  title,
}: CoachProps) => {
  const styles = useStyleSheet(themedStyle)
  const chatLabel = hasUnreadMessage ? 'Reply' : 'Chat'

  return (
    <TouchableOpacity
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={onPress ? 'button' : undefined}
      activeOpacity={0.8}
      disabled={!onPress}
      onPress={onPress}
      style={styles.container}
    >
      <View style={styles.content}>
        <View>
          {photo ? (
            <CoachAvatar photo={photo} style={styles.coachPhoto} />
          ) : (
            <View style={[styles.iconContainer, iconBackgroundStyle]}>
              {iconName && <Icon name={iconName} size={iconSize} style={styles.icon} />}
            </View>
          )}
          {hasUnreadMessage && <View style={styles.unreadMessageDot} />}
        </View>

        <View style={styles.copy}>
          <View style={styles.titleRow}>
            <Text bold lineSpacing="none" type="large" style={styles.title}>
              {title}
            </Text>
            {!!badgeLabel && (
              <View style={styles.badge}>
                <Text bold lineSpacing="none" type="small" style={styles.badgeText}>
                  {badgeLabel}
                </Text>
              </View>
            )}
          </View>

          <Text lineSpacing="none" type="small" style={styles.secondaryText}>
            {subtitle}
          </Text>
        </View>
      </View>

      <View style={styles.actions}>
        {onChatPress && (
          <TouchableOpacity
            accessibilityLabel={`${chatLabel} with ${title}`}
            accessibilityRole="button"
            activeOpacity={0.8}
            onPress={onChatPress}
            style={styles.chatButton}
          >
            <Icon
              name={hasUnreadMessage ? 'arrow-bend-up-left' : 'chat-circle-text'}
              weight="bold"
              size={16}
              style={styles.chatIcon}
            />
            <Text bold lineSpacing="none" type="regular">
              {chatLabel}
            </Text>
          </TouchableOpacity>
        )}

        {!!onPress && <Icon name="caret-right" size={20} weight="bold" style={styles.caret} />}
      </View>
    </TouchableOpacity>
  )
}

const themedStyle = StyleService.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 8,
  },
  copy: {
    flex: 1,
    gap: 4,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  title: {
    flexShrink: 1,
  },
  badge: {
    backgroundColor: 'theme.success.light_',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  badgeText: {
    color: 'theme.solid.white',
  },
  coachPhoto: {
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    color: 'theme.primary.dark',
  },
  unreadMessageDot: {
    position: 'absolute',
    top: 0,
    right: 4,
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: 'theme.error.base',
  },
  secondaryText: {
    color: 'theme.text.secondary',
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginLeft: 8,
  },
  chatButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderColor: 'theme.surface.base1',
    borderWidth: 2,
    borderRadius: 100,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  chatIcon: {
    color: 'theme.primary.base',
  },
  replyIcon: {
    width: 16,
    height: 16,
    tintColor: 'theme.primary.base',
  },
  caret: {
    color: 'theme.primary.base',
  },
})
